import { buildPanel } from 'features/drilldown/components/panelConfigs/PanelRenderUtils'
import { DEFAULT_COLUMN_WIDTH } from 'features/drilldown/data/savedExplorationState/explorationConstants'
import {
  DrilldownExplorationConfiguration,
  DrilldownPanelState,
  DrilldownViewState,
} from 'features/drilldown/types'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import startCase from 'lodash/startCase'

import {
  EXPLORATION_MUTATION_VERSION as explorationMutationVersion,
  ExplorationMutation,
  parseExplorationMutation,
} from './explorationMutationParser'
import type {
  ExplorationPanelSnapshot as PanelSnapshot,
  ExplorationSnapshot as Snapshot,
} from './explorationSnapshot'
import {
  buildExplorationSnapshot as buildSnapshot,
  EXPLORATION_SNAPSHOT_VERSION as explorationSnapshotVersion,
  getExplorationRevision as getRevision,
  snapshotPanel,
} from './explorationSnapshot'

export const EXPLORATION_MUTATION_VERSION = explorationMutationVersion
export const EXPLORATION_SNAPSHOT_VERSION = explorationSnapshotVersion
export type ExplorationPanelSnapshot = PanelSnapshot
export type ExplorationSnapshot = Snapshot

export const getExplorationRevision = (
  panels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
): string => getRevision(panels, configuration)

export const buildExplorationSnapshot = (
  explorationId: string,
  panels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
): ExplorationSnapshot => buildSnapshot(explorationId, panels, configuration)

export type ExplorationMutationApplyResult =
  | {
      ok: true
      panels: DrilldownViewState
      configuration: DrilldownExplorationConfiguration
      summary: string
    }
  | { ok: false; reason: string }

type CreateOperation = Extract<
  ExplorationMutation['operations'][number],
  { op: 'create' }
>

type ExecutedMutation = {
  panels: DrilldownViewState
  summaryChanges: string[]
  changedLayout: boolean
}

const canUpdate = (panel: DrilldownPanelState) =>
  panel.panelType !== 'columnBreak' && snapshotPanel(panel) !== undefined

const canRemove = (panel: DrilldownPanelState) =>
  snapshotPanel(panel) !== undefined

const getPanelTitle = (panel: DrilldownPanelState): string =>
  panel.panelData.title.trim() || startCase(panel.panelType)

const buildMutationSummary = (
  changes: ReadonlyArray<string>,
  changedLayout: boolean
): string => {
  const details = changedLayout
    ? [...changes, 'updated the Explorer layout']
    : changes
  return details.length > 0 ? `Updated Explorer: ${details.join('; ')}.` : ''
}

const buildMutationPanel = (operation: CreateOperation) => {
  const panel = buildPanel({ panelType: operation.panel.panelType })
  return {
    ...panel,
    panelData: merge(panel.panelData, operation.panel.initialDataOverride, {
      isConfigOpen: false,
    }),
  }
}

const validateMutationOperations = (
  mutation: ExplorationMutation,
  panels: DrilldownViewState
): string | undefined => {
  const panelsById = new Map(panels.map((panel) => [panel.id, panel]))
  const targetedIds = new Set<string>()
  const removedIds = new Set<string>()
  for (const operation of mutation.operations) {
    if (operation.op === 'create') continue
    const panel = panelsById.get(operation.panel_id)
    if (
      !panel ||
      targetedIds.has(operation.panel_id) ||
      (operation.op === 'update'
        ? !canUpdate(panel) ||
          operation.panel_data.panelDataSchemaVersion !==
            panel.panelData.panelDataSchemaVersion
        : !canRemove(panel))
    ) {
      return 'The AI targeted an invalid Explorer panel.'
    }
    targetedIds.add(operation.panel_id)
    if (operation.op === 'remove') removedIds.add(operation.panel_id)
  }
  for (const operation of mutation.operations) {
    if (operation.op !== 'create' || operation.after_panel_id === undefined) {
      continue
    }
    if (
      !panelsById.has(operation.after_panel_id) ||
      removedIds.has(operation.after_panel_id)
    ) {
      return 'The AI used an invalid panel insertion point.'
    }
  }
  return undefined
}

const executeMutationOperations = (
  mutation: ExplorationMutation,
  panels: DrilldownViewState
): ExecutedMutation => {
  const nextPanels = [...panels]
  const anchorOffsets = new Map<string, number>()
  const summaryChanges: string[] = []
  let changedLayout = false
  for (const operation of mutation.operations) {
    if (operation.op === 'create') {
      const newPanel = buildMutationPanel(operation)
      const anchorKey = operation.after_panel_id ?? '__end__'
      const anchorIndex = operation.after_panel_id
        ? nextPanels.findIndex((panel) => panel.id === operation.after_panel_id)
        : nextPanels.length - 1
      const offset = anchorOffsets.get(anchorKey) ?? 0
      nextPanels.splice(anchorIndex + 1 + offset, 0, newPanel)
      anchorOffsets.set(anchorKey, offset + 1)
      if (newPanel.panelType === 'columnBreak') {
        changedLayout = true
      } else summaryChanges.push(`created “${getPanelTitle(newPanel)}”`)
      continue
    }

    const panelIndex = nextPanels.findIndex(
      (panel) => panel.id === operation.panel_id
    )
    if (operation.op === 'remove') {
      if (nextPanels[panelIndex].panelType === 'columnBreak') {
        changedLayout = true
      } else {
        summaryChanges.push(
          `removed “${getPanelTitle(nextPanels[panelIndex])}”`
        )
      }
      nextPanels.splice(panelIndex, 1)
      continue
    }

    const updatedPanel = {
      ...nextPanels[panelIndex],
      panelData: cloneDeep(operation.panel_data),
    }
    nextPanels.splice(panelIndex, 1, updatedPanel)
    summaryChanges.push(`updated “${getPanelTitle(updatedPanel)}”`)
  }
  return { panels: nextPanels, summaryChanges, changedLayout }
}

const getMutationConfiguration = (
  panels: DrilldownViewState,
  nextPanels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
): DrilldownExplorationConfiguration => {
  const nextColumnCount =
    nextPanels.filter((panel) => panel.panelType === 'columnBreak').length + 1
  const currentColumnCount =
    panels.filter((panel) => panel.panelType === 'columnBreak').length + 1
  return nextColumnCount === currentColumnCount
    ? configuration
    : {
        ...configuration,
        columnWidths: Array(nextColumnCount).fill(DEFAULT_COLUMN_WIDTH),
      }
}

/** Validate all operations before applying them, so bad AI output is atomic. */
export const applyExplorationMutation = (
  value: unknown,
  explorationId: string,
  expectedRequestId: string,
  panels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
): ExplorationMutationApplyResult => {
  const mutation = parseExplorationMutation(value)
  if (!mutation) {
    return { ok: false, reason: 'The AI returned an invalid Explorer change.' }
  }
  if (mutation.dashboard_id !== explorationId) {
    return {
      ok: false,
      reason: 'This AI response belongs to another Explorer.',
    }
  }
  if (mutation.request_id !== expectedRequestId) {
    return { ok: false, reason: 'This AI response does not match the request.' }
  }
  if (mutation.revision !== getExplorationRevision(panels, configuration)) {
    return {
      ok: false,
      reason: 'The Explorer changed while the AI was working. Please retry.',
    }
  }

  const validationError = validateMutationOperations(mutation, panels)
  if (validationError) return { ok: false, reason: validationError }

  const executed = executeMutationOperations(mutation, panels)
  const nextConfiguration = getMutationConfiguration(
    panels,
    executed.panels,
    configuration
  )

  return {
    ok: true,
    panels: executed.panels,
    configuration: nextConfiguration,
    summary: buildMutationSummary(
      executed.summaryChanges,
      executed.changedLayout
    ),
  }
}
